import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DialogBackState } from './dialogBackTrail';

interface NavTarget {
    to: string;
    state?: DialogBackState;
}

/**
 * Guard FORWARD navigation to a related record the way `useUnsavedCloseGuard`
 * guards closing: a clean form navigates straight away, a dirty one raises the
 * shared `UnsavedChangesDialog` (keep editing / discard and go / save and go).
 * The close guard only covers back/close/reload, not an in-app push.
 *
 * Wiring in the host editor:
 *  - `guardedNav = useGuardedNavigate({ isDirty, submit: () => handleSubmit(onSubmit)(), close: () => closeGuard.close() })`
 *  - end `onSubmit` with `guardedNav.finishSubmit()` instead of `closeGuard.close()`
 *  - pass `guardedNav.requestNavigate` to the linking children
 *  - render `<UnsavedChangesDialog {...guardedNav.confirmProps} />`
 */
export function useGuardedNavigate({
    isDirty,
    submit,
    close,
    forwardState
}: {
    isDirty: boolean;
    /** `handleSubmit(onSubmit)()`. */
    submit: () => void | Promise<void>;
    /** The guard-aware close — `closeGuard.close()`. */
    close: () => void;
    /**
     * Default router state for onward navigation: the back-trail with this
     * dialog appended, so the target can offer a labelled "back" to here.
     * See `useForwardBackState`.
     */
    forwardState?: DialogBackState;
}) {
    const navigate = useNavigate();
    // The target a successful save should go to (set during "save and go").
    const pendingRef = useRef<NavTarget | null>(null);
    const [confirmTarget, setConfirmTarget] = useState<NavTarget | null>(null);

    /**
     * Leave for `target` — REPLACING the close guard's history sentinel when we
     * are standing on it.
     *
     * The sentinel sits on top of the dialog's own route entry and does not
     * change the URL, so pushing the next dialog on top left a stack of
     * [list, dialog A, sentinel A, dialog B] — and every close counts steps
     * (`useDialogClose` goes back over its own sentinel, plus one entry per
     * record paged past), so leaving dialog B landed on sentinel A and
     * re-opened the dialog the user had just left. Replacing it keeps one
     * entry per dialog, the open one's sentinel on top.
     */
    const go = (target: NavTarget) => {
        const onSentinel = Boolean(
            (window.history.state as { dialogGuard?: boolean } | null)
                ?.dialogGuard
        );
        navigate(target.to, {
            ...(target.state ? { state: target.state } : {}),
            replace: onSentinel
        });
    };

    return {
        /**
         * Leave for `to`; prompts first when the form is dirty. Forward links
         * omit `state` and inherit `forwardState`; the back button passes the
         * trimmed trail explicitly.
         */
        requestNavigate: (to: string, state?: DialogBackState) => {
            const target: NavTarget = { to, state: state ?? forwardState };
            if (isDirty) {
                setConfirmTarget(target);
            } else {
                go(target);
            }
        },
        /**
         * Leave for `to`, SAVING first when the form is dirty — no prompt.
         *
         * For the actions whose whole meaning is "this record is done, give me
         * the next one" ("Teil hinzufügen"), asking is a trap: "discard" would
         * drop the Teil mark just typed, and the sibling would be seeded off a
         * record that still claims to describe the whole Planum — the one state
         * the slot rule refuses (`partConflict`).
         *
         * A save that fails validation leaves the form standing and
         * `finishSubmit` never runs, so the jump waits for a stored record.
         */
        saveAndNavigate: async (to: string, state?: DialogBackState) => {
            const target: NavTarget = { to, state: state ?? forwardState };
            if (!isDirty) {
                go(target);
                return;
            }
            pendingRef.current = target;
            await submit();
            // Cleared when validation blocked the save; a no-op when it did not.
            pendingRef.current = null;
        },
        /**
         * Call at the END of `onSubmit` instead of the close: go to a pending
         * "save and go" target when there is one, else close normally.
         */
        finishSubmit: () => {
            const target = pendingRef.current;
            pendingRef.current = null;
            if (target) {
                go(target);
            } else {
                close();
            }
        },
        /** Spread onto an `<UnsavedChangesDialog />`. */
        confirmProps: {
            open: confirmTarget !== null,
            onKeepEditing: () => setConfirmTarget(null),
            onDiscard: () => {
                const target = confirmTarget;
                setConfirmTarget(null);
                if (target) {
                    go(target);
                }
            },
            onSave: async () => {
                const target = confirmTarget;
                setConfirmTarget(null);
                if (target) {
                    pendingRef.current = target;
                    await submit();
                    // Cleared when validation blocked the save; else a no-op.
                    pendingRef.current = null;
                }
            }
        }
    };
}
